import { FC, ReactNode } from "react";
import { Badge } from "components/Badge/Badge";
import CopyToClipboard from "components/CopyToClipboard/CopyToClipboard";
import {
  PaymentButtonSlot,
  PaymentFooter,
  type PaymentPolicyHelpPanelContext,
} from "components/PaymentButton";
import Skeleton from "components/Skeleton";
import { format } from "date-fns";
import { IconType } from "enums/Icon";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import formatter from "utils/formatter";
import {
  describeDepositFee,
  feeForGross,
  formatDepositFee,
  formatUsdt,
  sendAtLeast,
} from "utils/usdtDeposits";
import { getFnsLocale } from "../../translations/date-fns-locale";
import LoadFailedNotice from "./LoadFailedNotice";
import {
  Address,
  AddressRow,
  CardHeader,
  CheckStatus,
  CopyIcon,
  DepositCard,
  QR_BACKGROUND,
  QR_FOREGROUND,
  QrWrapper,
  Status,
  Summary,
  SummaryRow,
  SummaryTotalRow,
  Warnings,
} from "./UsdtDepositForm.styles";
import type { UsdtDepositState } from "./useUsdtDeposit";

export type UsdtDepositFormProps = {
  // useUsdtDeposit in the host, which also needs the deposits it tracks.
  deposit: UsdtDepositState;
  // Net amount the balance still needs (a purchase shortfall): shows the fee
  // and the USDT to send. Without it the card only states the rules.
  amountDue?: number;
  // Host progress line, e.g. what already arrived for a purchase.
  status?: ReactNode;
  // Replaces the Check payment button while the host is busy finishing.
  footer?: ReactNode;
  helpPanelContext?: PaymentPolicyHelpPanelContext;
};

const QR_SIZE = 168;

// Not memoized: the hook's state object is new on every host render.
const UsdtDepositForm: FC<UsdtDepositFormProps> = ({
  deposit,
  amountDue,
  status,
  footer,
  helpPanelContext,
}) => {
  const { t, i18n } = useTranslation("common");
  const currencySymbol = useCurrencySymbol();
  const {
    fee,
    minAmount,
    address,
    loadFailed,
    retryLoad,
    checkUntil,
    retryAt,
    checkPending,
    check,
  } = deposit;

  if (loadFailed) {
    return (
      <LoadFailedNotice
        message={t("usdtDeposit.loadFailed")}
        onRetry={retryLoad}
      />
    );
  }

  if (fee === null || address === null) {
    return (
      <PaymentButtonSlot aria-live="polite" role="status">
        <Skeleton height="48px" borderRadius="2px" />
      </PaymentButtonSlot>
    );
  }

  const time = (instant: string) =>
    format(new Date(instant), "HH:mm", {
      locale: getFnsLocale(i18n?.language),
    });
  const minLabel = `${formatUsdt(minAmount)} USDT`;

  let summary: ReactNode;
  if (amountDue !== undefined && amountDue > 0) {
    const send = sendAtLeast(amountDue, fee, minAmount);
    summary = (
      <Summary>
        <SummaryRow>
          <dt>{t("usdtDeposit.addToBalance")}</dt>
          <dd>
            {currencySymbol}
            {formatter.currency(amountDue)}
          </dd>
        </SummaryRow>
        <SummaryRow>
          <dt>{describeDepositFee(t, fee)}</dt>
          <dd>{formatUsdt(feeForGross(send, fee))} USDT</dd>
        </SummaryRow>
        <SummaryTotalRow>
          <dt>{t("usdtDeposit.sendAtLeast")}</dt>
          <dd>{formatUsdt(send)} USDT</dd>
        </SummaryTotalRow>
      </Summary>
    );
  } else {
    summary = (
      <Summary>
        {minAmount > 0 && (
          <SummaryRow>
            <dt>{t("usdtDeposit.minAmount")}</dt>
            <dd>{minLabel}</dd>
          </SummaryRow>
        )}
        <SummaryRow>
          <dt>{t("usdtDeposit.fee")}</dt>
          <dd>{formatDepositFee(fee)}</dd>
        </SummaryRow>
      </Summary>
    );
  }

  const checkStatus = checkUntil
    ? t("usdtDeposit.checkingUntil", { time: time(checkUntil) })
    : retryAt
      ? t("usdtDeposit.checkAgainAt", { time: time(retryAt) })
      : null;

  return (
    <>
      <DepositCard>
        <CardHeader>
          <Badge $color="pending">{address.network}</Badge>
          <span>{address.currency}</span>
        </CardHeader>
        <AddressRow>
          <Address>{address.address}</Address>
          <CopyToClipboard
            text={address.address}
            label={t("usdtDeposit.copyAddress")}
          >
            <CopyIcon type={IconType.Copy} />
          </CopyToClipboard>
        </AddressRow>
        <QrWrapper>
          <QRCodeSVG
            value={address.address}
            size={QR_SIZE}
            level="M"
            marginSize={2}
            fgColor={QR_FOREGROUND}
            bgColor={QR_BACKGROUND}
            title={address.address}
          />
        </QrWrapper>
        {summary}
        {status && (
          <Status role="status" aria-live="polite">
            {status}
          </Status>
        )}
        {checkStatus && (
          <CheckStatus role="status" aria-live="polite">
            {checkStatus}
          </CheckStatus>
        )}
        <Warnings>
          <li>{t("usdtDeposit.warnings.network")}</li>
          {minAmount > 0 && (
            <li>{t("usdtDeposit.warnings.minAmount", { min: minLabel })}</li>
          )}
          <li>{t("usdtDeposit.warnings.permanent")}</li>
        </Warnings>
      </DepositCard>
      <PaymentButtonSlot>
        {footer ?? (
          <PaymentFooter
            onClick={check}
            disabled={retryAt !== null || checkPending}
            isLoading={checkPending}
            helpPanelContext={helpPanelContext}
          >
            {t("usdtDeposit.checkPayment")}
          </PaymentFooter>
        )}
      </PaymentButtonSlot>
    </>
  );
};

export default UsdtDepositForm;
